import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, User, ShieldCheck, Eye, EyeOff } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { COMPANY } from "../lib/brand";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        if (form.password.length < 6) throw { response: { data: { detail: "Password must be at least 6 characters" } } };
        const { data } = await api.post("/auth/register", form);
        setUser(data.user);
        toast.success(`Welcome aboard, ${data.user.name.split(" ")[0]}!`);
      } else {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        setUser(data.user);
        toast.success("Welcome back!");
      }
      const me = (await api.get("/auth/me")).data;
      navigate(me.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-28 pb-24 ali-hero-gradient">
      <div className="max-w-md mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="ali-card p-8" data-testid="login-card">
          <div className="text-center">
            <h1 className="font-satoshi font-black text-3xl text-[#0A192F]">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h1>
            <p className="mt-2 text-slate-500 font-inter">
              {mode === "login" ? "Sign in to your A Learn India dashboard." : "Start learning with India's most loved coaches."}
            </p>
          </div>

          {/* Tabs */}
          <div className="mt-6 grid grid-cols-2 p-1 rounded-full bg-slate-100">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                data-testid={`tab-${m}`}
                className={`relative py-2.5 rounded-full font-poppins font-semibold text-sm transition ${
                  mode === m ? "text-white" : "text-slate-600"
                }`}
              >
                {mode === m && (
                  <motion.span
                    layoutId="auth-tab"
                    className="absolute inset-0 rounded-full bg-[#FF6B35]"
                    transition={{ type: "spring", duration: 0.4 }}
                  />
                )}
                <span className="relative">{m === "login" ? "Sign In" : "Register"}</span>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <AnimatePresence mode="wait">
              {mode === "register" && (
                <motion.div
                  key="name"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <label className="block text-xs uppercase tracking-widest text-slate-500 font-poppins mb-1.5">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      required={mode === "register"}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Aditi Sharma"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-[#FF6B35] font-inter"
                      data-testid="register-name"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 font-poppins mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-[#FF6B35] font-inter"
                  data-testid="auth-email"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 font-poppins mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  required
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={mode === "register" ? "At least 6 characters" : "Enter password"}
                  className="w-full pl-10 pr-11 py-3 rounded-xl border border-slate-200 outline-none focus:border-[#FF6B35] font-inter"
                  data-testid="auth-password"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="toggle-password"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full ali-btn-primary disabled:opacity-60"
              data-testid="auth-submit"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm font-inter text-slate-500">
            {mode === "login" ? (
              <>New to A Learn India?{" "}
                <button onClick={() => setMode("register")} className="text-[#FF6B35] font-poppins font-semibold" data-testid="switch-register">
                  Create an account →
                </button>
              </>
            ) : (
              <>Already a member?{" "}
                <button onClick={() => setMode("login")} className="text-[#FF6B35] font-poppins font-semibold" data-testid="switch-login">
                  Sign in →
                </button>
              </>
            )}
          </p>

          <p className="mt-5 text-center text-xs text-slate-400 font-inter flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Secure JWT authentication · 256-bit encryption
          </p>
        </motion.div>

        <p className="mt-6 text-center text-xs text-slate-400 font-inter">
          By signing up you agree to our <Link to="/terms" className="underline">Terms</Link> & <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </main>
  );
}
